import {
	type CommandConstructor,
	type CommandProperties,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

/**
 * Properties for the content copied command sent from an AIFC staging area iframe to the parent.
 */
export type AifcContentCopiedCommandProperties = CommandProperties & {
	/**
	 * The type of content from which the user copied.
	 */
	contentType: 'page' | 'whiteboard' | 'database' | 'slide' | 'slides';

	/**
	 * The host product's content identifier for this surface (e.g., Confluence contentId).
	 */
	contentId: string;

	/**
	 * Number of characters in the copied selection.
	 */
	copiedCharCount?: number;

	/**
	 * Number of whitespace-delimited words in the copied selection.
	 */
	copiedWordCount?: number;

	/**
	 * Total character count of the full document at copy time.
	 */
	totalDocCharCount?: number;

	/**
	 * Percentage (0–100) of the document that was copied, rounded to one decimal.
	 */
	copiedPercentage?: number;
};

/**
 * Command sent from an AIFC staging area iframe (page editor, whiteboard, database) to the
 * parent Rovo chat frame when the user copies content from the staging area.
 *
 * This allows the parent to fire a unified `aiResult actioned` analytics event regardless of
 * which content type is being previewed, without requiring cross-iframe DOM event detection.
 *
 * @summary {@link CommandProperties.senderId} is notifying {@link CommandProperties.receiverId}
 * that the user has copied content from the staging area preview.
 */
export const AifcContentCopiedCommand: CommandConstructor<AifcContentCopiedCommandProperties> =
	createCommand<AifcContentCopiedCommandProperties>('aifc-content-copied');
